import React, {
  useState,
  useRef,
  useEffect,
  ReactElement,
  CSSProperties,
  ReactNode,
  useCallback,
} from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import {
  RiInformationLine,
  RiAlertLine,
  RiCheckboxCircleLine,
  RiCloseFill,
} from '@remixicon/react';
import styles from './Popover.module.css';

export enum PopoverVariant {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
  DEFAULT = 'default',
}

export enum ArrowPosition {
  LEFT_TOP = 'left-top',
  LEFT_CENTER = 'left-center',
  LEFT_BOTTOM = 'left-bottom',
  RIGHT_TOP = 'right-top',
  RIGHT_CENTER = 'right-center',
  RIGHT_BOTTOM = 'right-bottom',
  TOP_LEFT = 'top-left',
  TOP_CENTER = 'top-center',
  TOP_RIGHT = 'top-right',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_CENTER = 'bottom-center',
  BOTTOM_RIGHT = 'bottom-right',
}

type PopoverProps = {
  isOpen: boolean;
  onClose: () => void;
  variant?: PopoverVariant;
  arrowPosition?: ArrowPosition;
  title: string;
  description: string;
  buttonLabel?: string;
  onButtonClick?: () => void;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: ReactElement<any>;
  style?: CSSProperties;
  showCloseButton?: boolean;
  showIcon?: boolean;
  customIcon?: ReactNode;
};

const getColorByVariant = (variant: PopoverVariant): string => {
  switch (variant) {
    case PopoverVariant.INFO:
      return '#0000C9';

    case PopoverVariant.WARNING:
      return '#823A0C';

    case PopoverVariant.ERROR:
      return '#A20F2C';

    case PopoverVariant.SUCCESS:
      return '#008253';

    case PopoverVariant.DEFAULT:
    default:
      return '#D4D4D8';
  }
};

const variantIcons = {
  [PopoverVariant.INFO]: (
    <RiInformationLine
      aria-hidden='true'
      color={getColorByVariant(PopoverVariant.INFO)}
      size={20}
    />
  ),
  [PopoverVariant.WARNING]: (
    <RiAlertLine
      aria-hidden='true'
      color={getColorByVariant(PopoverVariant.WARNING)}
      size={20}
    />
  ),
  [PopoverVariant.ERROR]: (
    <RiAlertLine
      aria-hidden='true'
      color={getColorByVariant(PopoverVariant.ERROR)}
      size={20}
    />
  ),
  [PopoverVariant.SUCCESS]: (
    <RiCheckboxCircleLine
      aria-hidden='true'
      color={getColorByVariant(PopoverVariant.SUCCESS)}
      size={20}
    />
  ),
  [PopoverVariant.DEFAULT]: (
    <RiInformationLine
      aria-hidden='true'
      color={getColorByVariant(PopoverVariant.DEFAULT)}
      size={20}
    />
  ),
};

const ANIMATION_DURATION = 200;

const Popover: React.FC<PopoverProps> = ({
  isOpen,
  onClose,
  variant = PopoverVariant.DEFAULT,
  arrowPosition = ArrowPosition.LEFT_CENTER,
  title,
  description,
  buttonLabel,
  onButtonClick,
  className = '',
  children,
  style = {},
  showCloseButton = false,
  showIcon = false,
  customIcon,
}) => {
  const [animationStage, setAnimationStage] = useState<'enter' | 'exit' | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const startExitAnimation = useCallback(() => {
    setAnimationStage('exit');
    setTimeout(() => {
      onClose();
    }, ANIMATION_DURATION);
  }, [onClose]);

  useOnClickOutside(containerRef, () => {
    if (isOpen) startExitAnimation();
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        startExitAnimation();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, startExitAnimation]);

  useEffect(() => {
    if (isOpen) {
      setAnimationStage('enter');
    }
  }, [isOpen]);

  const containerClass = [
    styles.popover,
    styles[variant],
    styles[arrowPosition],
    animationStage === 'enter' && styles.popoverEnter,
    animationStage === 'exit' && styles.popoverExit,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const arrowClass = [styles.arrow, styles[`arrow-${arrowPosition}`]]
    .filter(Boolean)
    .join(' ')
    .trim();

  const arrowDirection = arrowPosition.split('-')[0];

  return (
    <div
      ref={containerRef}
      style={{ display: 'inline-block', position: 'relative' }}
    >
      {React.cloneElement(children, {
        'aria-describedby': 'popover-content',
      })}

      {(isOpen || animationStage === 'exit') && (
        <div
          role='alert'
          id='popover-content'
          className={containerClass}
          style={
            {
              ...style,
              '--popover-color': getColorByVariant(PopoverVariant.DEFAULT),
            } as CSSProperties & { [key: string]: string }
          }
          aria-live='polite'
          aria-hidden={!isOpen}
          data-variant={variant}
        >
          {showIcon && (
            <div className={styles.icon}>
              {customIcon || variantIcons[variant]}
            </div>
          )}

          <div className={styles.content}>
            <div className={styles.title}>{title}</div>
            <div className={styles.description}>{description}</div>
            {buttonLabel && (
              <button
                onClick={onButtonClick}
                className={styles.button}
                aria-label={buttonLabel}
              >
                {buttonLabel}
              </button>
            )}
          </div>

          {showCloseButton && (
            <button
              aria-label='Close popover'
              className={styles.closeButton}
              onClick={startExitAnimation}
            >
              <RiCloseFill color='#18181B' size={24} />
            </button>
          )}

          <div className={arrowClass} data-direction={arrowDirection} />
        </div>
      )}
    </div>
  );
};

export default Popover;
